/**
 * DEMO-ONLY HTTP handlers for ICM product stock/price notifications (LOCAL_MOCK).
 */
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import {
  createPresidioMockProductNotification,
  deletePresidioMockProductNotification,
  getPresidioMockProductNotification,
  listPresidioMockProductNotificationLinks,
  PresidioMockProductNotification,
  PresidioMockProductNotificationType,
  updatePresidioMockProductNotification,
} from './presidio-mock-product-notifications';

/** customers/{customerNo}/users/{user}/notifications/{stock|price}[/{sku}] */
const B2B_PATH =
  /^customers\/([^/]+)\/users\/[^/]+\/notifications\/(stock|price)(?:\/([^/]+))?$/i;
/** privatecustomers/-/notifications/{stock|price}[/{sku}] */
const B2C_PATH = /^privatecustomers\/-\/notifications\/(stock|price)(?:\/([^/]+))?$/i;

export function handlePresidioMockProductNotifications(
  req: HttpRequest<unknown>,
  restPath: string,
  readJsonBody: <T>(body: unknown) => T
): Observable<HttpEvent<unknown>> | null {
  let customerNo = 'PRESIDIO-MOCK';
  let type: PresidioMockProductNotificationType | undefined;
  let sku: string | undefined;

  const b2b = B2B_PATH.exec(restPath);
  if (b2b) {
    customerNo = decodeURIComponent(b2b[1]);
    type = b2b[2].toLowerCase() as PresidioMockProductNotificationType;
    sku = b2b[3] ? decodeURIComponent(b2b[3]) : undefined;
  } else {
    const b2c = B2C_PATH.exec(restPath);
    if (!b2c) {
      return null;
    }
    type = b2c[1].toLowerCase() as PresidioMockProductNotificationType;
    sku = b2c[2] ? decodeURIComponent(b2c[2]) : undefined;
  }

  if (type !== 'stock' && type !== 'price') {
    return null;
  }

  const method = req.method.toUpperCase();

  if (!sku && method === 'GET') {
    return of(
      new HttpResponse({
        status: 200,
        body: listPresidioMockProductNotificationLinks(customerNo, type),
        url: req.url,
      })
    );
  }

  if (!sku && method === 'POST') {
    const body = readJsonBody<Partial<PresidioMockProductNotification>>(req.body);
    try {
      const link = createPresidioMockProductNotification(customerNo, type, body);
      return of(new HttpResponse({ status: 201, body: link, url: req.url }));
    } catch {
      return of(
        new HttpResponse({
          status: 400,
          body: { error: 'sku required' },
          url: req.url,
        })
      );
    }
  }

  if (sku && method === 'GET') {
    const found = getPresidioMockProductNotification(type, sku);
    if (!found) {
      // No alert yet — empty 404 would surface as a UI error; return soft empty for PDP probes.
      return of(new HttpResponse({ status: 404, body: {}, url: req.url }));
    }
    return of(
      new HttpResponse({
        status: 200,
        body: {
          sku: found.sku,
          notificationMailAddress: found.notificationMailAddress,
          ...(found.price ? { price: found.price } : {}),
        },
        url: req.url,
      })
    );
  }

  if (sku && method === 'PUT') {
    const body = readJsonBody<Partial<PresidioMockProductNotification>>(req.body);
    const updated = updatePresidioMockProductNotification(type, sku, body);
    return of(
      new HttpResponse({
        status: 200,
        body: {
          sku: updated.sku,
          notificationMailAddress: updated.notificationMailAddress,
          ...(updated.price ? { price: updated.price } : {}),
        },
        url: req.url,
      })
    );
  }

  if (sku && method === 'DELETE') {
    deletePresidioMockProductNotification(type, sku);
    return of(new HttpResponse({ status: 204, body: null, url: req.url }));
  }

  return null;
}
